/**
 * WaitlistForm.tsx — ɳSentry early-access waitlist signup form
 *
 * Purpose:    Captures email addresses for the ɳSentry early-access waitlist.
 *             Submits to /api/waitlist and shows referral link on success.
 * Inputs:     None — form is self-contained
 * Outputs:    Form with 4-state machine: idle, loading, success, error/duplicate
 * Constraints: No auth required; email is the only required field
 */
'use client'

import { useState } from 'react'

// UI State machine:
// idle      → empty form, ready for input
// loading   → submitting to API
// success   → email accepted, referral link shown
// error     → submission failed
// duplicate → email already on waitlist
// ratelimit → too many attempts today

type State = 'idle' | 'loading' | 'success' | 'error' | 'duplicate' | 'ratelimit'

interface SuccessData {
  referralCode: string
  referralUrl: string
}

function generateReferralUrl(code: string): string {
  return `https://family.nself.org/invite?ref=${code}`
}

export function WaitlistForm() {
  const [email, setEmail] = useState('')
  const [state, setState] = useState<State>('idle')
  const [successData, setSuccessData] = useState<SuccessData | null>(null)
  const [errorMessage, setErrorMessage] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!email || state === 'loading') return

    setState('loading')

    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      const data = await res.json() as { error?: string; referralCode?: string }

      if (res.status === 429) {
        setState('ratelimit')
        return
      }

      if (res.status === 409) {
        setState('duplicate')
        return
      }

      if (!res.ok) {
        setErrorMessage(data.error ?? 'Something went wrong. Please try again.')
        setState('error')
        return
      }

      const referralCode = data.referralCode ?? ''
      setSuccessData({
        referralCode,
        referralUrl: generateReferralUrl(referralCode),
      })
      setState('success')
    } catch {
      setErrorMessage('Could not reach the server. Please try again.')
      setState('error')
    }
  }

  // --- UI State: success ---
  if (state === 'success' && successData) {
    return (
      <div className="rounded-xl border border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-950/20 p-8 text-center">
        <div className="mb-4 flex justify-center">
          <svg className="h-12 w-12 text-emerald-700 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
          </svg>
        </div>
        <h3 className="mb-2 text-xl font-bold text-brand-ink">You are on the list</h3>
        <p className="mb-6 text-brand-muted">
          We will send an invite code to <strong className="text-brand-ink">{email}</strong> when ɳFamily ships.
        </p>
        <div className="rounded-lg border border-brand-subtle bg-brand-bg p-4 text-left">
          <p className="mb-2 text-xs font-medium uppercase tracking-widest text-brand-muted">
            Share with your family
          </p>
          <p className="mb-3 text-sm text-brand-secondary">
            Your referral link, family members who sign up with this link will be grouped with you at launch.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded border border-brand-card bg-brand-surface px-3 py-2 font-mono text-xs text-indigo-700 dark:text-indigo-300 break-all">
              {successData.referralUrl}
            </code>
            <button
              onClick={() => navigator.clipboard.writeText(successData.referralUrl)}
              className="flex-shrink-0 rounded border border-brand-card px-3 py-2 text-xs text-brand-muted hover:bg-brand-surface-2 hover:text-brand-ink transition-colors"
            >
              Copy
            </button>
          </div>
        </div>
      </div>
    )
  }

  // --- UI State: rate-limited ---
  if (state === 'ratelimit') {
    return (
      <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/20 p-8 text-center">
        <h3 className="mb-2 text-lg font-bold text-amber-700 dark:text-amber-300">Already signed up</h3>
        <p className="text-brand-muted">
          This email is already on the waitlist or has been submitted today. Check your inbox for a confirmation
          email. If you need help,{' '}
          <a href="mailto:info@nself.org" className="text-indigo-700 dark:text-indigo-400 hover:text-indigo-700 dark:text-indigo-300">
            contact us
          </a>
          .
        </p>
      </div>
    )
  }

  // --- UI State: duplicate ---
  if (state === 'duplicate') {
    return (
      <div className="rounded-xl border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-950/20 p-8 text-center">
        <h3 className="mb-2 text-lg font-bold text-amber-700 dark:text-amber-300">Already on the list</h3>
        <p className="text-brand-muted">
          <strong className="text-brand-ink">{email}</strong> is already signed up. Check your inbox for the
          confirmation email with your referral link.
        </p>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* UI State: error */}
      {state === 'error' && (
        <div className="rounded-lg border border-red-200 dark:border-red-800/50 bg-red-50 dark:bg-red-950/20 p-4 text-sm text-red-700 dark:text-red-300">
          {errorMessage}{' '}
          <a href="mailto:info@nself.org" className="underline hover:text-red-700 dark:text-red-200">
            Email us directly
          </a>{' '}
          if this keeps happening.
        </div>
      )}

      {/* UI States: idle + loading + error (form stays visible) */}
      <div>
        <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-brand-secondary">
          Email address
        </label>
        <input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          placeholder="you@example.com"
          disabled={state === 'loading'}
          className="w-full rounded-md border border-brand-card bg-brand-surface px-4 py-2.5 text-brand-ink placeholder-gray-600 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
        />
      </div>

      <button
        type="submit"
        disabled={state === 'loading' || !email}
        className="w-full rounded-md bg-brand-primary px-4 py-2.5 text-sm font-medium text-white hover:bg-brand-hover transition-colors disabled:cursor-not-allowed disabled:opacity-50"
      >
        {state === 'loading' ? (
          <span className="flex items-center justify-center gap-2">
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Joining waitlist...
          </span>
        ) : (
          'Join the waitlist'
        )}
      </button>

      <p className="text-center text-xs text-brand-muted">
        No spam. One email when ɳFamily ships. Unsubscribe anytime.
      </p>
    </form>
  )
}
